/* eslint-disable react-refresh/only-export-components */
/**
 * RunProvider: supplies latest run and runs list to the app (header, Buyer Ops, etc.).
 * - Initial load: getMyLastRun(workflowId) (latest completed) + getMyActiveRun(workflowId)
 *   (any in-progress/transforms-running) to backend gateway, no pagination. The active-run
 *   query is needed because getMyLastRun only returns RUN_COMPLETED and the subscription
 *   doesn't replay an already-running row to a fresh tab. (CHO-667)
 * - Updates: AppSync subscribeToNewRuns keeps runs in sync.
 * - Self-heal: on window focus / tab-visible, re-fetch + reconcile to recover from any
 *   subscription event missed while the tab was backgrounded.
 * See plan: getMyLastRun replaces full getMyRuns pagination to reduce init queueing.
 */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useCustomer } from "../../hooks/useCustomer";
import { GetMyActiveRunDocument, GetMyLastRunDocument } from "../../services/__generated__/graphql";
import { appSyncService } from "../../services/appsync/service";
import { RunStatus, type Run } from "../../services/appsync/types";
import { apolloClient } from "../../services/backend_gateway/apollo-client";
import { runSettingsObjectToCanonicalJson } from "../../utils/runSettings";
import { WORKFLOW_ID, mapBackendRunStatus } from "./constants";
import type { RunContextType } from "./types";

export const RunContext = createContext<RunContextType | undefined>(undefined);

// --- Cross-tab pending-run lock (CHO-667) ---------------------------------
// The optimistic lock set when a run is fired. Backed by localStorage (shared
// across all same-origin tabs + survives refresh) and pushed live between open
// tabs via BroadcastChannel, so a click in one tab disables the Run button
// everywhere. A timestamp enforces a timeout across refreshes/tabs.
const PENDING_RUN_TIMEOUT_MS = 60_000;
const PENDING_STORAGE_PREFIX = "cho667:pendingRun:";
const PENDING_BROADCAST_CHANNEL = "cho667-pending-run";

const pendingStorageKey = (customerId: string): string =>
  `${PENDING_STORAGE_PREFIX}${customerId}`;

const readPersistedPending = (customerId: string): string | null => {
  try {
    const raw = localStorage.getItem(pendingStorageKey(customerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { value?: unknown; ts?: unknown };
    if (typeof parsed.value !== "string" || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > PENDING_RUN_TIMEOUT_MS) {
      localStorage.removeItem(pendingStorageKey(customerId));
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
};

const writePersistedPending = (customerId: string, value: string | null): void => {
  try {
    const key = pendingStorageKey(customerId);
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify({ value, ts: Date.now() }));
  } catch {
    // storage unavailable (private mode / quota) — degrade to in-memory + BroadcastChannel.
  }
};

// Shared mapper: backend-gateway Run shape → AppSync Run shape.
type BackendRun = {
  runSettings: Parameters<typeof runSettingsObjectToCanonicalJson>[0];
  status: Parameters<typeof mapBackendRunStatus>[0];
  createdAt: string;
  updatedAt: string;
  stepIds?: string | null;
  initiatedBy?: string | null;
  name?: string | null;
  description?: string | null;
};
const backendRunToRun = (backendRun: BackendRun, organizationId: string): Run => ({
  runSettings: runSettingsObjectToCanonicalJson(backendRun.runSettings),
  organizationId,
  status: mapBackendRunStatus(backendRun.status),
  createdAt: backendRun.createdAt,
  updatedAt: backendRun.updatedAt,
  stepIds: backendRun.stepIds ?? "",
  initiatedBy: backendRun.initiatedBy ?? null,
  name: backendRun.name ?? null,
  description: backendRun.description ?? null,
});

// Latest *completed* run for the workflow (header, view transforms).
const fetchLastRun = async (organizationId: string): Promise<Run[]> => {
  const result = await apolloClient.query({
    query: GetMyLastRunDocument,
    variables: { workflowId: WORKFLOW_ID },
  });
  const backendRun = result.data?.getMyLastRun ?? null;
  if (!backendRun) return [];
  return [backendRunToRun(backendRun, organizationId)];
};

// Any *active* run (in-progress / transforms-running). getMyLastRun only returns
// RUN_COMPLETED and the subscription doesn't replay an already-running row to a
// fresh tab, so this is the authoritative load-time "is a run active?" signal,
// also used to self-heal stale long-open tabs on focus. (CHO-667)
const fetchActiveRun = async (organizationId: string): Promise<Run[]> => {
  const result = await apolloClient.query({
    query: GetMyActiveRunDocument,
    variables: { workflowId: WORKFLOW_ID },
    fetchPolicy: "network-only",
  });
  const backendRun = result.data?.getMyActiveRun ?? null;
  if (!backendRun) return [];
  return [backendRunToRun(backendRun, organizationId)];
};

// Sort runs by createdAt descending (most recent first).
const sortRuns = (runsList: Run[]): Run[] => {
  return [...runsList].sort((a, b) => {
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();
    return bTime - aTime;
  });
};

// Pick the more recently updated of two rows that share a runSettings key.
// Prefer the incoming row (b) on a tie so identical re-deliveries are idempotent.
const newerRun = (a: Run, b: Run): Run => {
  const aTime = new Date(a.updatedAt).getTime();
  const bTime = new Date(b.updatedAt).getTime();
  return bTime >= aTime ? b : a;
};

// Merge incoming runs into the current list, keyed by runSettings (the unique
// run identity). On conflict the row with the newer updatedAt wins, so a live
// subscription update is never clobbered by a staler initial fetch — and the
// initial fetch likewise can't overwrite a fresher subscription row,
// regardless of which writer lands first. (CHO-667 follow-up: setRuns race)
const upsertRuns = (currentRuns: Run[], incoming: Run[]): Run[] => {
  const byKey = new Map(currentRuns.map((r) => [r.runSettings, r]));
  for (const run of incoming) {
    const existing = byKey.get(run.runSettings);
    byKey.set(run.runSettings, existing ? newerRun(existing, run) : run);
  }
  return sortRuns([...byKey.values()]);
};

export const RunProvider = ({ children }: { children: ReactNode }) => {
  const { selectedCustomerId } = useCustomer();
  const [runs, setRuns] = useState<Run[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  // Cross-tab pending-run lock (CHO-667). Lazy-init from localStorage so a tab
  // opened/refreshed mid-fire already shows the lock.
  const [pendingRunSettings, setPendingRunSettingsState] = useState<string | null>(() =>
    readPersistedPending(selectedCustomerId)
  );
  const pendingChannelRef = useRef<BroadcastChannel | null>(null);

  // Set the lock locally + persist to localStorage + broadcast to other tabs.
  const broadcastPending = useCallback(
    (value: string | null) => {
      setPendingRunSettingsState(value);
      writePersistedPending(selectedCustomerId, value);
      pendingChannelRef.current?.postMessage({ customerId: selectedCustomerId, value });
    },
    [selectedCustomerId]
  );
  const markRunPending = useCallback(
    (runSettingsIdentity: string) => broadcastPending(runSettingsIdentity),
    [broadcastPending]
  );
  const clearPendingRun = useCallback(() => broadcastPending(null), [broadcastPending]);

  // Subscribe to cross-tab lock changes: BroadcastChannel for live push between
  // open tabs, the storage event as a fallback, and a re-read on customer switch.
  useEffect(() => {
    setPendingRunSettingsState(readPersistedPending(selectedCustomerId));

    const channel =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel(PENDING_BROADCAST_CHANNEL)
        : null;
    pendingChannelRef.current = channel;
    if (channel) {
      channel.onmessage = (e: MessageEvent) => {
        const msg = e.data as { customerId?: string; value?: string | null } | null;
        if (!msg || msg.customerId !== selectedCustomerId) return;
        // Apply directly (don't re-broadcast — avoids an echo loop).
        setPendingRunSettingsState(msg.value ?? null);
      };
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key !== pendingStorageKey(selectedCustomerId)) return;
      setPendingRunSettingsState(readPersistedPending(selectedCustomerId));
    };
    window.addEventListener("storage", onStorage);

    return () => {
      channel?.close();
      pendingChannelRef.current = null;
      window.removeEventListener("storage", onStorage);
    };
  }, [selectedCustomerId]);

  // Clear the lock once the matching run row arrives via AppSync (string equality
  // on canonical-JSON runSettings).
  useEffect(() => {
    if (pendingRunSettings === null) return;
    if (runs.some((r) => r.runSettings === pendingRunSettings)) {
      clearPendingRun();
    }
  }, [runs, pendingRunSettings, clearPendingRun]);

  // Timeout fallback: never lock the button forever if the run row never lands.
  useEffect(() => {
    if (pendingRunSettings === null) return;
    const handle = window.setTimeout(() => clearPendingRun(), PENDING_RUN_TIMEOUT_MS);
    return () => window.clearTimeout(handle);
  }, [pendingRunSettings, clearPendingRun]);

  // Derive latestRun from the runs list (most recent first after sorting).
  // useMemo instead of useState+useEffect avoids the extra render and keeps
  // latestRun in lockstep with runs. (CHO-667 follow-up: derived-state-in-state)
  const latestRun = useMemo(() => (runs.length > 0 ? runs[0] : null), [runs]);

  // Compute latestSuccessfulRun for workflow_atomic_pivots
  const latestSuccessfulRun = useMemo(() => {
    return (
      runs.find((run) => {
        if (run.status !== RunStatus.RUN_COMPLETED) {
          return false;
        }
        try {
          const runSettings = JSON.parse(run.runSettings);
          return runSettings.workflow_id === WORKFLOW_ID;
        } catch {
          return false;
        }
      }) ?? null
    );
  }, [runs]);

  useEffect(() => {
    // Reset state when customer changes (latestRun is derived from runs)
    setRuns([]);
    setIsLoading(true);
    setError(null);

    // Clean up existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    // Set up subscription to new runs and fetch initial latest run
    // Note: organizationId will be determined by the service based on user role and token
    // For ROOT users, it uses selectedCustomerId; for others, it uses custom:organization from token
    const setupSubscription = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // PERF: Run subscription setup and initial data fetch in parallel for ~1-2s improvement
        // These are independent operations - subscription can start while we fetch historical data
        const subscriptionSetupPromise = appSyncService.subscribeToNewRuns(
          { organizationId: selectedCustomerId }, // Placeholder, will be overridden by service
          (run: Run) => {
            // Update runs list (handles both new runs and updates to existing
            // runs). latestRun is derived from runs via useMemo.
            setRuns((currentRuns) => upsertRuns(currentRuns, [run]));
            setIsLoading(false);
          },
          (err: Error) => {
            console.error("Error in run subscription:", err);
            setError(err);
            setIsLoading(false);
          }
        );

        // Wait for all three operations to complete in parallel
        const [subscription, fetchedRuns, fetchedActiveRuns] = await Promise.allSettled([
          subscriptionSetupPromise,
          fetchLastRun(selectedCustomerId),
          fetchActiveRun(selectedCustomerId),
        ]);

        // Handle subscription result
        if (subscription.status === "fulfilled") {
          subscriptionRef.current = subscription.value;
        } else {
          console.error("Error setting up run subscription:", subscription.reason);
          setError(
            subscription.reason instanceof Error
              ? subscription.reason
              : new Error("Failed to set up subscription")
          );
        }

        // Handle fetch result. Merge (don't replace) so a subscription update
        // that arrived before this fetch resolved isn't clobbered. newer
        // updatedAt wins on conflict. (CHO-667 follow-up: setRuns race)
        if (fetchedRuns.status === "fulfilled") {
          setRuns((currentRuns) => upsertRuns(currentRuns, fetchedRuns.value));
        } else {
          // Silently handle getLastRun errors - subscription will still work
          const errorMessage =
            fetchedRuns.reason instanceof Error
              ? fetchedRuns.reason.message
              : String(fetchedRuns.reason);
          if (
            !errorMessage.includes("MappingTemplate") &&
            !errorMessage.includes("Unable to parse")
          ) {
            console.warn("Error fetching initial run (non-critical):", fetchedRuns.reason);
          }
        }

        // Seed any active (in-progress / transforms-running) run so the button
        // gate is correct on load, before the subscription delivers updates.
        // (CHO-667)
        if (fetchedActiveRuns.status === "fulfilled") {
          if (fetchedActiveRuns.value.length > 0) {
            setRuns((currentRuns) => upsertRuns(currentRuns, fetchedActiveRuns.value));
          }
        } else {
          console.warn(
            "Error fetching active run (non-critical):",
            fetchedActiveRuns.reason
          );
        }

        setIsLoading(false);
      } catch (err) {
        console.error("Error setting up run subscription:", err);
        setError(err instanceof Error ? err : new Error("Failed to set up subscription"));
        setIsLoading(false);
      }
    };

    setupSubscription();

    // Cleanup function
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [selectedCustomerId]);

  // Self-heal on focus / tab-visible: after the initial load the AppSync
  // subscription is the only updater, so a missed event (reconnect/token gap)
  // leaves a long-open tab with a stale in-progress row (button stuck disabled)
  // or a missed active run (stale enabled). Re-fetch the authoritative active +
  // last run when the user returns to the tab and reconcile: drop any in-memory
  // active rows, then re-add the fetched truth. (CHO-667)
  useEffect(() => {
    let cancelled = false;
    const reconcile = async () => {
      const [last, active] = await Promise.allSettled([
        fetchLastRun(selectedCustomerId),
        fetchActiveRun(selectedCustomerId),
      ]);
      // Only reconcile when the authoritative active-run query succeeded —
      // otherwise we'd risk dropping a valid in-progress row on a transient error.
      if (cancelled || active.status !== "fulfilled") return;
      const fresh: Run[] = [
        ...(last.status === "fulfilled" ? last.value : []),
        ...active.value,
      ];
      setRuns((currentRuns) => {
        const withoutActive = currentRuns.filter(
          (r) =>
            r.status !== RunStatus.RUN_IN_PROGRESS
            && r.status !== RunStatus.RUN_TRANSFORMS_RUNNING
        );
        return upsertRuns(withoutActive, fresh);
      });
    };

    const onFocus = () => void reconcile();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void reconcile();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [selectedCustomerId]);

  return (
    <RunContext.Provider
      value={{
        latestRun,
        latestSuccessfulRun,
        runs,
        isLoading,
        error,
        pendingRunSettings,
        markRunPending,
        clearPendingRun,
      }}
    >
      {children}
    </RunContext.Provider>
  );
};
